import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { I18N_KEYS } from '@/language/keys';
import Actions from '@/components/Menu/Actions';
import { useStickyNoteView } from '../FlowContext';
import { DEFAULT_STICKY_NOTE_COLOR, StickyNoteSize } from '../Views/StickyNoteView';
import ParentMenuItem from './ParentMenuItem';
import { ColorMenuItem } from './menu-items/ColorMenuItem';
import type { MenuAction } from '@/components/Menu/Actions';
import type { NodeId } from 'web';

interface StickyNoteContextMenuProps {
  positionX: number;
  positionY: number;
  isOpen: boolean;
  onClose: () => void;
  stickyNoteNodeId: NodeId;
}

export const StickyNoteContextMenu = ({
  positionX,
  positionY,
  isOpen,
  onClose,
  stickyNoteNodeId,
}: StickyNoteContextMenuProps) => {
  const { t } = useTranslation();
  const stickyNoteView = useStickyNoteView(stickyNoteNodeId);
  const currentSize = stickyNoteView.getSize();

  const handleColorChange = useCallback(
    (color: string) => {
      stickyNoteView.updateColor(color);
    },
    [stickyNoteView],
  );

  const handleSizeChange = useCallback(
    (size: StickyNoteSize) => {
      stickyNoteView.updateSize(size);
      onClose();
    },
    [stickyNoteView, onClose],
  );

  const currentColor = stickyNoteView.getColor() || DEFAULT_STICKY_NOTE_COLOR;

  const menu: MenuAction[] = useMemo(
    () => [
      {
        type: 'slot' as const,
        el: <ColorMenuItem currentColor={currentColor} onColorChange={handleColorChange} />,
      },
      {
        type: 'slot' as const,
        el: renderFontSizeMenuItem(t, handleSizeChange, currentSize),
      },
    ],
    [t, currentColor, handleColorChange, currentSize, handleSizeChange],
  );

  return (
    <AppContextMenu open={isOpen} onClose={onClose} mouseX={positionX} mouseY={positionY} items={menu} width="200px" />
  );
};

function renderFontSizeMenuItem(
  t: (key: string) => string,
  handleSizeChange: (size: StickyNoteSize) => void,
  currentSize: StickyNoteSize,
) {
  return (
    <ParentMenuItem
      label={t(I18N_KEYS.RECIPE_MAIN.NODES.STICKY_NOTE.CONTEXT_MENU.FONT_SIZE)}
      offset={{ left: '10px' }}
      contextMenuWidth="180px"
    >
      <Actions
        items={[
          {
            name: t(I18N_KEYS.RECIPE_MAIN.NODES.STICKY_NOTE.CONTEXT_MENU.FONT_SIZE_OPTIONS.SMALL),
            action: () => handleSizeChange(StickyNoteSize.SM),
            selected: currentSize === StickyNoteSize.SM,
          },
          {
            name: t(I18N_KEYS.RECIPE_MAIN.NODES.STICKY_NOTE.CONTEXT_MENU.FONT_SIZE_OPTIONS.MEDIUM),
            action: () => handleSizeChange(StickyNoteSize.MD),
            selected: currentSize === StickyNoteSize.MD,
          },
          {
            name: t(I18N_KEYS.RECIPE_MAIN.NODES.STICKY_NOTE.CONTEXT_MENU.FONT_SIZE_OPTIONS.LARGE),
            action: () => handleSizeChange(StickyNoteSize.LG),
            selected: currentSize === StickyNoteSize.LG,
          },
        ]}
      />
    </ParentMenuItem>
  );
}
